import * as React from "react";
import { cn } from "../../../lib/utils";

/**
 * Spinner Component - Drop-in replacement for antd Spin
 *
 * @example
 * <Spinner size="lg" />
 *
 * // With wrapping content
 * <Spinner spinning={isLoading}>
 *   <ContentToWrap />
 * </Spinner>
 */
const Spinner = ({
  size = "default",
  className,
  spinning = true,
  children,
  tip,
}) => {
  const sizeClasses = {
    sm: "h-4 w-4 border-2",
    default: "h-8 w-8 border-2",
    lg: "h-12 w-12 border-3",
  };

  const spinnerElement = (
    <div
      className={cn(
        "animate-spin rounded-full border-yellow-400 border-t-transparent",
        sizeClasses[size] || sizeClasses.default,
        className
      )}
      role="status"
      aria-label="Loading"
    />
  );

  // If no children, just return the spinner
  if (!children) {
    return spinning ? (
      <div className="flex flex-col items-center justify-center gap-2">
        {spinnerElement}
        {tip && <span className="text-sm text-gray-500 dark:text-gray-400">{tip}</span>}
      </div>
    ) : null;
  }

  // With children, wrap them with optional overlay
  return (
    <div className="relative">
      {spinning && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 dark:bg-[#141414]/70 z-10 rounded">
          {spinnerElement}
          {tip && <span className="mt-2 text-sm text-gray-500 dark:text-gray-400">{tip}</span>}
        </div>
      )}
      <div className={spinning ? "opacity-50 pointer-events-none" : ""}>
        {children}
      </div>
    </div>
  );
};

export { Spinner };
